import {
  getCurrentAuthRevision,
  getEffectiveAuthProvider,
} from './effectiveAuthProvider';

export interface AuthIdentitySnapshot {
  provider: string;
  authRevision: number;
}

/**
 * The ONE definition of "current auth identity" — effective provider
 * (`getEffectiveAuthProvider`: cookie-first on web, Redux/persisted-first on
 * desktop) paired with the runtime-only `authRevision`. Every consumer that
 * decides whether a captured `{provider, authRevision}` context is still
 * current — request-context capture, the auth-cache's own commit gates,
 * per-caller acceptance, and the axios destructive-exit fence — MUST read
 * through this, never Redux `authProvider` directly: on web the effective
 * provider is cookie-first and can diverge from Redux during a
 * migration/sync window, so a raw Redux read can reject a still-current
 * request or miss an effective-provider change Redux hasn't caught up to.
 *
 * Reads through `effectiveAuthProvider.ts` for BOTH fields (never `@/store`
 * directly) — that module is the one already-coupled facade for the `auth`
 * Redux slice this file's own dependents are allowed to reach; a new direct
 * `@/store` import here would trip the Rematch containment ratchet
 * (`@heidi/rules/no-new-rematch-coupling`).
 *
 * Import boundary: this module transitively reaches `@/store` (via
 * `effectiveAuthProvider.ts`), which several HTTP-adjacent modules
 * (`scribeHTTP.ts`, `globalHTTP.ts`, `scribeHttpErrorHandler.ts`,
 * `getAuthTokenForHttpClient.ts`) cannot import statically — `@/store`
 * transitively reaches back to those files through the Redux model graph
 * (`store.ts` → `models` → `tauriRecordModel.ts` → `scribeHTTP.ts`). Those
 * modules must keep reaching this one through a lazy
 * `await import('@/lib/auth/authIdentitySnapshot')`, matching the existing
 * `@/lib/auth/effectiveAuthProvider` lazy-import pattern they already use.
 * Modules already inside the `@/lib/auth` band (e.g. `getAccessToken.ts`,
 * which already imports `@/store` directly) may import this statically.
 */
export function getCurrentAuthIdentitySnapshot(): AuthIdentitySnapshot {
  return {
    provider: getEffectiveAuthProvider() ?? '',
    authRevision: getCurrentAuthRevision(),
  };
}
